import { useMutation } from '@apollo/client';
import type { InsertRiskAssessmentResultsMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  InsertRiskAssessmentResultsDocument,
  namedOperations,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import type { InsertRiskAssessmentResultInput } from './useInsertRiskAssessmentResultTRPC';
import { useInsertRiskAssessmentResultTRPC } from './useInsertRiskAssessmentResultTRPC';

export const useInsertRiskAssessmentResult = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  // GraphQL mutation
  const [insertRiskAssessmentResultGraphQL, graphqlState] = useMutation(
    InsertRiskAssessmentResultsDocument,
    {
      update: (cache) => {
        evictField(cache, 'risk_assessment_result');
        evictField(cache, 'assessment');
        evictField(cache, 'risk_assessment_result_aggregate');
      },
      refetchQueries: [namedOperations.Query.getRiskScoresByRiskId],
    }
  );

  // tRPC mutation
  const trpcMutation = useInsertRiskAssessmentResultTRPC();

  const insertRiskAssessmentResult = async (
    variables: InsertRiskAssessmentResultInput
  ): Promise<InsertRiskAssessmentResultsMutation> => {
    if (trpcEnabled) {
      return trpcMutation.insertRiskAssessmentResult(variables);
    }

    const result = await insertRiskAssessmentResultGraphQL({
      variables,
    });
    if (!result.data) {
      throw new Error('Failed to insert risk assessment result');
    }

    return result.data;
  };

  // Return appropriate state based on feature flag
  if (trpcEnabled) {
    return {
      insertRiskAssessmentResult,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    insertRiskAssessmentResult,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
